import { geoMercator, geoPath } from 'd3-geo'
import { select, event as d3event, mouse as d3mouse } from 'd3-selection'
import { zoom, zoomIdentity } from 'd3-zoom'
import { scaleOrdinal } from 'd3-scale'
import { feature } from 'topojson'
import { sumBy, range, uniqBy } from 'lodash'
import worldMapJson from './worldMap.json'
import cluster from 'density-clustering'

const radiusScale = scaleOrdinal()
  .domain(range(2, 10).map((n) => `${n}`))
  .range(range(10, 20))

export default class WorldMap {
  constructor({ dimensions, node, cities, onCityClick }) {
    this.dimensions = dimensions
    this.node = node
    this.cities = cities
    this.tooltipId = 'world-map-tooltip'
    this.transform = zoomIdentity
    this.onCityClick = onCityClick

    // Append svg and layer containers
    this.container = select(node).style('position', 'relative')
    this.svg = this.container.append('svg')
    this.mapLayer = this.svg.append('g').attr('id', 'map-layer')
    this.pinLayer = this.svg.append('g').attr('id', 'pin-layer')

    this.svg
      .append('defs')
      .append('linearGradient')
      .attr('id', 'fill')
      .selectAll('stop')
      .data([
        { offset: 0, color: '#128aef' },
        { offset: 0.5, color: '#128aef' },
        { offset: 1, color: '#24c7a3' },
      ])
      .enter()
      .append('stop')
      .attr('offset', (d) => d.offset)
      .attr('stop-color', (d) => d.color)

    this.initMapHelpers()
  }

  update({ dimensions, node, cities }) {
    if (node) this.node = node
    if (dimensions) this.dimensions = dimensions
    if (cities) this.cities = cities

    this.initMapHelpers()
  }

  draw() {
    this.updateMap()
    this.updatePins()
  }

  initMapHelpers() {
    const { width, height } = this.dimensions
    const worldData = feature(worldMapJson, worldMapJson.objects.countries1)

    this.projection = geoMercator().fitExtent(
      [
        [20, 20],
        [width, height],
      ],
      worldData
    )

    const pathGenerator = geoPath().projection(this.projection)

    this.paths = worldData.features.map((country) => ({
      props: country.properties,
      path: pathGenerator(country),
    }))

    this.transform = zoomIdentity
    this.zoom = zoom()
      .scaleExtent([0.8, 50])
      .translateExtent([
        [-10, -10],
        [width + 10, height + 10],
      ])
      .on('zoom', () => {
        this.transform = d3event.transform
        this.draw()
      })
      .filter(() => {
        const { type, ctrlKey, altKey } = d3event
        // Ignore zoom on scroll event unsleass shift, ctrl or meta key
        // is pressed.
        return type === 'wheel' ? altKey || ctrlKey : true
      })
  }

  updateMap() {
    const { dimensions, container, svg, mapLayer, paths, transform, tooltipId } = this
    svg
      .attr('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`)
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .call(this.zoom)

    const countries = mapLayer
      .attr('transform', transform)
      .selectAll('.country')
      .data(paths)
      .style('stroke-width', 1)

    countries
      .enter()
      .append('path')
      .classed('country', true)
      .attr('d', ({ path }) => path)
      .style('fill', '#ddd')
      .style('stroke', '#ddd')
      .style('stroke-width', '1px')
      .on('mouseenter', function () {
        select(this).transition().duration(100).style('fill', '#ccc').style('stroke', '#ccc')
      })
      .on('mousemove', function () {
        const d = select(this).datum()
        const mouse = d3mouse(container.node())
        let tooltip = container.select(`#${tooltipId}`)
        let label = tooltip.select('span')

        if (!tooltip.node()) {
          tooltip = container
            .append('div')
            .attr('id', tooltipId)
            .style('position', 'absolute')
            .style('top', 0)
            .style('left', 0)
            .style('pointer-events', 'none')
            .style('background', 'rgba(0,0,0,0.75)')
            .style('padding', '3px 7px')
            .style('border-radius', '4px')
            .style('color', 'white')
          label = tooltip.append('span')
        }

        tooltip.style('transform', `translate(${mouse[0] + 10}px, ${mouse[1] - 10}px)`)
        label.text(d.props.name)
      })
      .on('mouseleave', function () {
        select(this).transition().duration(100).style('fill', '#ddd').style('stroke', '#ddd')

        container.select(`#${tooltipId}`).remove()
      })

    countries.attr('d', ({ path }) => path)
    countries.exit().remove()
  }

  updatePins(cities) {
    const scope = this
    const { projection, transform, tooltipId, container, pinLayer } = this
    const mapObjects = this.clusterCities({
      cities,
      projection: projection,
      transform: transform,
    })

    const pins = pinLayer
      .selectAll('.pin')
      .data(mapObjects, (d) => d.map(({ data: { name } }) => name).join('-'))

    pins
      .enter()
      .append('g')
      .attr('class', 'pin')
      .style('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('transform', (d) => {
        const { x, y } = scope.calculateClusterPos(d)
        return `translate(${x}px, ${y}px)`
      })
      .on('click', (d) => this.onCityClick(d.map(({ data }) => data)))
      .on('mousemove', function () {
        const d = select(this).datum()
        const mouse = d3mouse(container.node())
        let tooltip = container.select(`#${tooltipId}`)

        if (!tooltip.node()) {
          tooltip = container
            .append('div')
            .attr('id', tooltipId)
            .style('position', 'absolute')
            .style('top', 0)
            .style('left', 0)
            .style('pointer-events', 'none')
            .style('background', 'rgba(0,0,0,0.75)')
            .style('padding', '3px 7px')
            .style('border-radius', '4px')
            .style('color', 'white')
        }
        tooltip.style('transform', `translate(${mouse[0] + 10}px, ${mouse[1]}px)`)

        const labels = tooltip.selectAll('div').data(d)
        labels
          .enter()
          .append('div')
          .text((d) => d.data.name)

        labels.text((d) => d.data.name)
        labels.exit().remove()
      })
      .on('mouseleave', function () {
        container.select(`#${tooltipId}`).remove()
      })
      .each(function (d) {
        const g = select(this)
        d.length === 1 ? scope.renderPin(g) : scope.renderGroup(g)
      })

    pins
      .style('transform', (d) => {
        const { x, y } = scope.calculateClusterPos(d)
        return `translate(${x}px, ${y}px)`
      })
      .each(function (d) {
        const g = select(this)
        d.length === 1 ? scope.renderPin(g) : scope.renderGroup(g)
      })

    pins.exit().remove()
  }

  renderPin(g) {
    const [d] = g.datum()
    let innerG = g.select('#innerG')

    if (!innerG.node()) {
      innerG = g.append('g').attr('id', 'innerG').style('opacity', 1).style('cursor', 'pointer')

      innerG.transition().duration(300).style('opacity', 1)

      // innerG
      //   .append('circle')
      //   .attr('r', 5)
      //   .attr('cy', 1)
      //   .style('stroke-width', 2)
      //   .style('stroke', 'rgba(0,0,0,0.2)')
      //   .style('fill', 'none')

      innerG
        .append('circle')
        .attr('r', 5)
        .style('fill', 'url(#fill)')
        .style('stroke-width', 1)
        .style('stroke', 'white')
    }

    innerG.select('rect').remove()

    let text = innerG.select('text')

    if (!text.node()) {
      text = innerG
        .append('text')
        .attr('y', 5)
        .attr('dy', '1em')
        .style('font-size', '14px')
        .style('transform', 'translate(0px, 0px)')
        .style('-webkit-transform', 'translate(0px, 0px)')
        .text(d.data.name)
    }
  }

  renderGroup(g) {
    const d = g.datum()
    let innerG = g.select('#innerG')

    if (!innerG.node()) {
      innerG = g.append('g').attr('id', 'innerG').style('opacity', 1).style('cursor', 'pointer')

      // innerG
      //   .append('circle')
      //   .attr('r', radiusScale(d.length))
      //   .attr('cy', 1)
      //   .style('stroke-width', 2)
      //   .style('stroke', 'rgba(0,0,0,0.2)')
      //   .style('fill', 'none')

      innerG
        .append('circle')
        .attr('r', radiusScale(d.length))
        .style('fill', 'url(#fill)')
        .style('stroke-width', 1)
        .style('stroke', 'white')

      innerG.append('path').attr('d', 'M0 0h24v24H0z').style('fill', 'transparent')

      innerG
        .append('text')
        .attr('dy', 3)
        .style('fill', 'white')
        .style('font-weight', '700')
        .style('transform', 'translate(0px, 0px)')
        .style('-webkit-transform', 'translate(0px, 0px)')
        .text(d.length)
    }
  }

  calculateClusterPos(cluster) {
    return {
      x: sumBy(cluster, 'x') / cluster.length,
      y: sumBy(cluster, 'y') / cluster.length,
    }
  }

  clusterCities() {
    const { cities, projection, transform: t } = this
    const radius = 34
    const arg = 1
    // hack to get rid of duplicate cities
    const points = uniqBy(cities, 'name').map((c) => {
      const [x, y] = projection([c.long, c.lat])
      return [x * t.k + t.x, y * t.k + t.y]
    })
    const dbscan = new cluster.DBSCAN()
    const clusteredData = dbscan.run(points, radius, arg).reduce(
      (result, cluster) => [
        ...result,
        cluster.map((i) => ({
          data: cities[i],
          x: points[i][0],
          y: points[i][1],
        })),
      ],
      []
    )

    return clusteredData
  }

  zoomIn() {
    this.zoom.scaleTo(this.svg.transition().duration(350), Math.min(2 * this.transform.k, 50))
  }

  zoomOut() {
    this.zoom.scaleTo(this.svg.transition().duration(350), Math.max(this.transform.k / 2, 0.8))
  }
}
